import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { EditVoiceCloneConfig } from './voice-cloning-operations';
import {
    deleteVoiceClone,
    editVoiceClone,
    getVoiceCloneLimit,
    startVoiceClone,
    startVoiceCloneFromUrl,
    waitForVoiceCloneResult,
} from './voice-cloning-operations';
import VoiceCloningConfigureStep from './VoiceCloningConfigureStep';
import VoiceCloningRecordStep from './VoiceCloningRecordStep';
import VoiceCloningUploadStep from './VoiceCloningUploadStep';

import type { AIVoice } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import DashboardActionButton from 'js/src/components/common/ui/DashboardActionButton';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import {
    resetAIVoiceModal,
    upsertAIVoice,
} from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-slice';
import type { VoiceCloningModalContext } from 'js/src/data/modals';
import {
    getModalContext,
    hideModal,
    isVoiceCloningModalShowing,
    LOCKED_FEATURES,
    MODAL_IDS,
    showModal,
} from 'js/src/data/modals';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { useAppDispatch } from 'js/src/libs/RootState';
import { getBlobDuration } from 'js/src/libs/video-duration-tools';

const MIN_AUDIO_DURATION_SECONDS = 10;

enum VoiceCloningStep {
    LOADING = 'loading',
    SELECT_METHOD = 'select_method',
    UPLOAD = 'upload',
    RECORD = 'record',
    CONFIGURE = 'configure',
}

const STEP_TITLES: Record<VoiceCloningStep, string> = {
    [VoiceCloningStep.LOADING]: 'Create an AI voice',
    [VoiceCloningStep.SELECT_METHOD]: 'Create an AI voice',
    [VoiceCloningStep.UPLOAD]: 'Upload your voice to create an AI voiceover',
    [VoiceCloningStep.RECORD]: 'Record your voice to create an AI voiceover',
    [VoiceCloningStep.CONFIGURE]: 'Create an AI voiceover',
};

const FROM_VOICEOVER_TITLE = 'Create an AI voice from your recording';

const VoiceCloningModal: React.FC = () => {
    const isModalShowing = useSelector(isVoiceCloningModalShowing);
    const modalContext = useSelector(getModalContext<VoiceCloningModalContext>);
    const returnToVoiceSelection = modalContext?.toJS()['returnToVoiceSelection'];
    const gcsUrl = modalContext?.get('gcsUrl') ?? undefined;
    const dispatch = useAppDispatch();

    const isFromVoiceover = Boolean(gcsUrl);
    const postLoadingStep = isFromVoiceover
        ? VoiceCloningStep.CONFIGURE
        : VoiceCloningStep.SELECT_METHOD;

    const [step, setStep] = useState<VoiceCloningStep>(VoiceCloningStep.LOADING);
    const audioBlobRef = useRef<Blob | null>(null);
    const previousStepRef = useRef<VoiceCloningStep>(VoiceCloningStep.SELECT_METHOD);

    const [clonedVoice, setClonedVoice] = useState<AIVoice | null>(null);
    const [cloneError, setCloneError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const wasSavedRef = useRef(false);
    const savedVoiceSlugRef = useRef<string | null>(null);

    const isCloning =
        step === VoiceCloningStep.CONFIGURE && !clonedVoice && !cloneError && !isSaving;

    useEffect(() => {
        if (isModalShowing) return;
        setStep(VoiceCloningStep.LOADING);
        setClonedVoice(null);
        setCloneError(null);
        setIsSaving(false);
        audioBlobRef.current = null;
    }, [isModalShowing]);

    useEffect(() => {
        if (!isModalShowing) return;

        analytics.track('Voice Cloning: Modal Opened', {
            entry_point: isFromVoiceover ? 'voiceover_recording' : 'voice_selection',
        });

        const abortController = new AbortController();
        setStep(VoiceCloningStep.LOADING);

        getVoiceCloneLimit(abortController.signal)
            .then(({ count, limit }) => {
                if (abortController.signal.aborted) return;
                if (count >= limit) {
                    analytics.track('Voice Cloning: Limit Reached', {
                        current_count: count,
                        team_limit: limit,
                    });
                    dispatch(hideModal());
                    dispatch(
                        showModal({
                            modal_id: MODAL_IDS.FEATURE_LOCK,
                            modal_context: ImmutableFromJS({
                                featureName: LOCKED_FEATURES.VOICE_CLONING_LIMIT,
                            }),
                        }),
                    );
                    return;
                }
                setStep(postLoadingStep);
            })
            .catch(() => {
                if (abortController.signal.aborted) return;
                setStep(postLoadingStep);
            });

        return () => {
            abortController.abort();
        };
    }, [isModalShowing, postLoadingStep, dispatch, isFromVoiceover]);

    const handleClose = useCallback(() => {
        if (!wasSavedRef.current && clonedVoice) {
            void deleteVoiceClone(clonedVoice.slug, { silent: true });
        }

        dispatch(hideModal());
        dispatch(resetAIVoiceModal());
        if (returnToVoiceSelection) {
            const context = savedVoiceSlugRef.current
                ? { ...returnToVoiceSelection, initialSelectedVoiceId: savedVoiceSlugRef.current }
                : returnToVoiceSelection;
            dispatch(
                showModal({
                    modal_id: MODAL_IDS.AI_VOICE_SELECTION,
                    modal_context: ImmutableFromJS(context),
                }),
            );
        }
        setStep(VoiceCloningStep.SELECT_METHOD);
        audioBlobRef.current = null;
        wasSavedRef.current = false;
        savedVoiceSlugRef.current = null;
        setClonedVoice(null);
        setCloneError(null);
    }, [clonedVoice, dispatch, returnToVoiceSelection]);

    const handleBack = useCallback(() => {
        if (isFromVoiceover) {
            handleClose();
            return;
        }
        if (step === VoiceCloningStep.CONFIGURE) {
            setStep(previousStepRef.current);
        } else {
            setStep(VoiceCloningStep.SELECT_METHOD);
        }
    }, [isFromVoiceover, step, handleClose]);

    const advanceToConfigure = useCallback(
        (blob: Blob, fromStep: VoiceCloningStep) => {
            audioBlobRef.current = blob;
            previousStepRef.current = fromStep;
            wasSavedRef.current = false;
            if (clonedVoice) {
                void deleteVoiceClone(clonedVoice.slug, { silent: true });
            }
            setClonedVoice(null);
            setCloneError(null);
            setStep(VoiceCloningStep.CONFIGURE);
        },
        [clonedVoice],
    );

    useEffect(() => {
        if (step !== VoiceCloningStep.CONFIGURE) return;
        if (clonedVoice || cloneError) return;

        if (!audioBlobRef.current && !gcsUrl) return;

        const abortController = new AbortController();

        async function run() {
            const startTime = Date.now();
            const audioSource = gcsUrl
                ? 'voiceover'
                : previousStepRef.current === VoiceCloningStep.UPLOAD
                  ? 'upload'
                  : 'record';
            try {
                analytics.track('Voice Cloning: Started', { audio_source: audioSource });
                const taskId = gcsUrl
                    ? await startVoiceCloneFromUrl(gcsUrl, abortController.signal)
                    : await startVoiceClone(audioBlobRef.current!, abortController.signal);
                const voice = await waitForVoiceCloneResult(taskId, abortController);
                if (!abortController.signal.aborted) {
                    setClonedVoice(voice);
                    analytics.track('Voice Cloning: Succeeded', {
                        audio_source: audioSource,
                        duration_ms: Date.now() - startTime,
                    });
                }
            } catch (err) {
                if (abortController.signal.aborted) return;
                if (err instanceof DOMException && err.name === 'AbortError') return;
                analytics.track('Voice Cloning: Failed', {
                    audio_source: audioSource,
                    error_message: err instanceof Error ? err.message : String(err),
                });
                setCloneError('Failed to create AI voice. Please try again.');
                Toast.error('Failed to create AI voice. Please try again.');
            }
        }

        void run();

        return () => {
            abortController.abort();
        };
    }, [step, clonedVoice, cloneError, gcsUrl]);

    const handleFilesSelected = useCallback(
        async (files: File[]) => {
            if (files.length === 0) return;
            const file = files[0];
            try {
                const duration = await getBlobDuration(file);
                if (duration < MIN_AUDIO_DURATION_SECONDS) {
                    Toast.error(
                        `Recording is too short — please upload at least ${MIN_AUDIO_DURATION_SECONDS} seconds of audio.`,
                    );
                    return;
                }
            } catch {
                Toast.error("Couldn't read the audio file. Please try a different clip.");
                return;
            }
            advanceToConfigure(file, VoiceCloningStep.UPLOAD);
        },
        [advanceToConfigure],
    );

    const handleRecordingComplete = useCallback(
        async (blob: Blob) => {
            try {
                const duration = await getBlobDuration(blob);
                if (duration < MIN_AUDIO_DURATION_SECONDS) {
                    Toast.error(
                        `Recording is too short — please record at least ${MIN_AUDIO_DURATION_SECONDS} seconds.`,
                    );
                    return;
                }
            } catch {
                Toast.error("Couldn't read the recording. Please try again.");
                return;
            }
            advanceToConfigure(blob, VoiceCloningStep.RECORD);
        },
        [advanceToConfigure],
    );

    const handleSelectMethod = useCallback((method: 'upload' | 'record') => {
        analytics.track('Voice Cloning: Method Selected', { method });
        setStep(method === 'upload' ? VoiceCloningStep.UPLOAD : VoiceCloningStep.RECORD);
    }, []);

    const handleSaveVoice = useCallback(
        async (config: EditVoiceCloneConfig) => {
            if (!clonedVoice || isSaving) return;
            setIsSaving(true);
            try {
                const updated = await editVoiceClone(clonedVoice.slug, config);
                if (updated) {
                    dispatch(upsertAIVoice(updated));
                    analytics.track('Voice Cloning: Voice Saved', {
                        voice_slug: updated.slug,
                        has_tone: Boolean(config.tone),
                        voice_name_length: config.name.length,
                    });
                    wasSavedRef.current = true;
                    savedVoiceSlugRef.current = updated.slug;
                    handleClose();
                    return;
                }
                Toast.error('Failed to save AI voice. Please try again.');
            } finally {
                setIsSaving(false);
            }
        },
        [clonedVoice, isSaving, handleClose, dispatch],
    );

    const showing = isModalShowing && step !== VoiceCloningStep.LOADING;

    const showBackButton =
        !isFromVoiceover &&
        step !== VoiceCloningStep.SELECT_METHOD &&
        step !== VoiceCloningStep.LOADING &&
        !isCloning;

    const title = useMemo(
        () => (isFromVoiceover ? FROM_VOICEOVER_TITLE : STEP_TITLES[step]),
        [isFromVoiceover, step],
    );

    return (
        <Modal
            title={title}
            size={MODAL_SIZES.LG}
            showing={showing}
            onHidden={handleClose}
            onMoveBack={showBackButton ? handleBack : undefined}
            closeable={!isCloning}
            keyboard={!isCloning}
            backdrop={isCloning ? 'static' : undefined}
            loadingContents={isCloning}
        >
            {step === VoiceCloningStep.SELECT_METHOD && (
                <div className="d-flex flex-column gap-4">
                    <p className="font-l5 font-l5--small--medium color-grey-600 m-l5-0">
                        Train an AI voice using a short recording of yourself speaking. Upload a
                        clip of you speaking clearly or record one now (at least 10 seconds).
                    </p>
                    <div className="d-flex gap-3">
                        <DashboardActionButton
                            className="flex-grow-1"
                            icon="upload"
                            iconClassName="color-grey-600"
                            title="Upload a voiceover"
                            onClick={() => handleSelectMethod('upload')}
                            testId="voice-cloning-upload-option"
                        />
                        <DashboardActionButton
                            className="flex-grow-1"
                            icon="mic"
                            iconClassName="color-error-500"
                            title="Record a voiceover"
                            onClick={() => handleSelectMethod('record')}
                            testId="voice-cloning-record-option"
                        />
                    </div>
                </div>
            )}
            {step === VoiceCloningStep.UPLOAD && (
                <VoiceCloningUploadStep onFilesSelected={handleFilesSelected} />
            )}
            {step === VoiceCloningStep.RECORD && (
                <VoiceCloningRecordStep onRecordingComplete={handleRecordingComplete} />
            )}
            {step === VoiceCloningStep.CONFIGURE && (
                <VoiceCloningConfigureStep
                    clonedVoice={clonedVoice}
                    cloneError={cloneError}
                    isSaving={isSaving}
                    onBack={handleBack}
                    onSave={handleSaveVoice}
                    secondaryLabel={isFromVoiceover ? 'Cancel' : 'Back'}
                />
            )}
        </Modal>
    );
};

export default VoiceCloningModal;
